import {
    FOLDER_DASHBOARD_BASIC_INFO_PROPERTIES_KEY,
    FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY,
    SERVICE_DASHBOARD_WIDGET_SETTINGS_KEY,
} from '@libs/types/src/services/types/folder-dashboard-settings.type';
import { DashboardGridsterItem, gridsterOptions } from '../modules/shared/constants/dashboard-settings';

export function prepareDataToSave(data: any, key: string): any {
    switch (key) {
        case FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY:
        case SERVICE_DASHBOARD_WIDGET_SETTINGS_KEY:
            // we must store only info about position and size, not about min size, handlers, ...
            return Object.fromEntries(
                Object.entries(data).map(([key, value]: [string, any]) => [
                    key,
                    {
                        name: value.name,
                        x: value.x,
                        y: value.y,
                        cols: value.cols,
                        rows: value.rows,
                    },
                ]),
            );
        case FOLDER_DASHBOARD_BASIC_INFO_PROPERTIES_KEY:
            return data;
    }
}

export function prepareWidgetDataToView(
    items: Record<string, DashboardGridsterItem>,
    loadedFromDB: Record<string, Partial<DashboardGridsterItem>> = {},
    pinnedPositions: Record<string, Pick<DashboardGridsterItem, 'x' | 'y'>> = {},
): Record<string, DashboardGridsterItem> {
    const occupiedCells = new Set<string>();
    const pinnedCells = new Set<string>();
    const preparedItems: Record<string, DashboardGridsterItem> = {};
    const itemsWithoutValidPosition: Array<[string, DashboardGridsterItem]> = [];
    const maxCols = Number(gridsterOptions.maxCols ?? Number.MAX_SAFE_INTEGER);
    const itemEntries = Object.entries(items).sort(([keyA], [keyB]) => {
        const pinnedA = pinnedPositions[keyA] != null;
        const pinnedB = pinnedPositions[keyB] != null;

        if (pinnedA === pinnedB) return 0;

        return pinnedA ? -1 : 1;
    });

    for (const [key, defaultItem] of itemEntries) {
        const savedItem = loadedFromDB[key];
        const pinnedPosition = pinnedPositions[key];
        const mergedItem = { ...defaultItem, ...savedItem, ...pinnedPosition };

        if (
            pinnedPosition
            && isValidGridsterPosition(mergedItem, maxCols)
        ) {
            markOccupiedCells(mergedItem, occupiedCells);
            markOccupiedCells(mergedItem, pinnedCells);
            preparedItems[key] = mergedItem;
        } else if (
            hasSavedGridsterPosition(savedItem)
            && isValidGridsterPosition(mergedItem, maxCols)
            && !collidesWithOccupiedCells(mergedItem, pinnedCells)
        ) {
            markOccupiedCells(mergedItem, occupiedCells);
            preparedItems[key] = mergedItem;
        } else {
            itemsWithoutValidPosition.push([key, mergedItem]);
        }
    }

    for (const [key, item] of itemsWithoutValidPosition) {
        const positionedItem = moveToFirstAvailablePosition(item, occupiedCells, maxCols);

        markOccupiedCells(positionedItem, occupiedCells);
        preparedItems[key] = positionedItem;
    }

    return preparedItems;
}

export function removeDashboardItemsFromLayout(
    layout: Record<string, Partial<DashboardGridsterItem>>,
    itemNames: readonly string[],
): Record<string, Partial<DashboardGridsterItem>> {
    const removedItemNames = new Set(itemNames);

    return Object.fromEntries(
        Object.entries(layout).filter(([name]) => !removedItemNames.has(name)),
    );
}

function hasSavedGridsterPosition(item: Partial<DashboardGridsterItem> | undefined): item is Partial<DashboardGridsterItem> & Pick<DashboardGridsterItem, 'x' | 'y'> {
    return item?.x != null && item?.y != null;
}

function isValidGridsterPosition(item: DashboardGridsterItem, maxCols: number): boolean {
    return item.x >= 0
        && item.y >= 0
        && item.cols > 0
        && item.rows > 0
        && item.x + item.cols <= maxCols;
}

function moveToFirstAvailablePosition(
    item: DashboardGridsterItem,
    occupiedCells: Set<string>,
    maxCols: number,
): DashboardGridsterItem {
    const positionedItem = { ...item };

    const lastPossibleX = Math.max(0, maxCols - positionedItem.cols);

    for (let y = 0; ; y++) {
        for (let x = 0; x <= lastPossibleX; x++) {
            positionedItem.x = x;
            positionedItem.y = y;

            if (!collidesWithOccupiedCells(positionedItem, occupiedCells)) {
                return positionedItem;
            }
        }
    }
}

function collidesWithOccupiedCells(
    item: DashboardGridsterItem,
    occupiedCells: Set<string>,
): boolean {
    return getGridsterItemCells(item).some((cell) => occupiedCells.has(cell));
}

function markOccupiedCells(
    item: DashboardGridsterItem,
    occupiedCells: Set<string>,
): void {
    for (const cell of getGridsterItemCells(item)) {
        occupiedCells.add(cell);
    }
}

function getGridsterItemCells(item: DashboardGridsterItem): string[] {
    const cells: string[] = [];

    for (let y = item.y; y < item.y + item.rows; y++) {
        for (let x = item.x; x < item.x + item.cols; x++) {
            cells.push(`${x}:${y}`);
        }
    }

    return cells;
}
